import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { SKYLINE_IMG } from "../lib/site";

const areas = ["Chicago", "Oak Park", "Cicero", "Berwyn", "Evanston", "Skokie", "Naperville", "Oak Lawn", "Elmhurst", "Downers Grove"];

// Approximate coverage boundary enclosing Chicago + listed suburbs
const COVERAGE = [
  [42.075, -87.68],  // Evanston / lakefront north
  [42.06, -87.82],   // Skokie / Niles
  [41.99, -88.02],   // Elmhurst / Elk Grove
  [41.83, -88.20],   // Naperville (west)
  [41.72, -88.14],   // Downers Grove / SW
  [41.63, -87.95],   // Oak Lawn / SW
  [41.60, -87.75],   // south suburbs
  [41.64, -87.52],   // lakefront south
  [41.90, -87.58],   // lakefront mid
  [42.075, -87.60],  // lakefront north
];

const CoverageMap = () => {
  const el = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    if (map.current || !el.current) return;
    map.current = L.map(el.current, {
      center: [41.86, -87.75],
      zoom: 10,
      scrollWheelZoom: false,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19,
    }).addTo(map.current);

    L.polygon(COVERAGE, {
      color: "#C6A15B",
      weight: 2.5,
      fillColor: "#C6A15B",
      fillOpacity: 0.14,
    })
      .addTo(map.current)
      .bindTooltip("MB Notary Coverage Area", { sticky: true });

    L.circleMarker([41.8781, -87.6298], {
      radius: 7,
      color: "#1F1F1F",
      weight: 2,
      fillColor: "#C6A15B",
      fillOpacity: 1,
    })
      .addTo(map.current)
      .bindTooltip("Chicago", { permanent: false });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return <div ref={el} className="w-full h-[360px] sm:h-[420px]" data-testid="map-canvas" />;
};

export const ServiceArea = () => {
  return (
    <section id="service-area" className="relative bg-taupe/60 py-24 lg:py-32 overflow-hidden" data-testid="service-area-section">
      {/* skyline silhouette */}
      <img
        src={SKYLINE_IMG}
        alt="Chicago skyline"
        className="absolute bottom-0 left-0 w-full h-2/3 object-cover object-bottom opacity-25 mix-blend-multiply pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ivory via-transparent to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="text-goldDark" size={20} strokeWidth={1.5} />
              <p className="eyebrow text-goldDark">Service Area</p>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink leading-[1.05]">
              Serving Chicago &<br /><span className="gold-text italic">Surrounding Suburbs</span>
            </h2>
            <span className="block w-16 h-px bg-goldDark mt-6 mb-6" />
            <p className="text-ink/75 font-light leading-relaxed max-w-md">
              Providing mobile notary services throughout Chicago and nearby suburbs.
              If you are outside my normal service area, please reach out — I am happy to
              discuss availability and travel arrangements.
            </p>
            <a href="#booking" className="btn-gold rounded-full px-8 py-4 text-xs font-medium tracking-widest uppercase inline-flex items-center gap-2 mt-8" data-testid="area-book-btn">
              Request A Visit <ArrowUpRight size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="bg-ivory/80 backdrop-blur-sm rounded-2xl border border-gold/25 p-8 sm:p-10 shadow-xl"
          >
            <p className="eyebrow text-goldDark mb-6">Areas I Cover</p>
            <div className="flex flex-wrap gap-3">
              {areas.map((a) => (
                <span key={a} className="rounded-full border border-taupe/50 bg-white/70 px-4 py-2 text-sm text-ink/80 hover:border-gold hover:text-goldDark transition-colors" data-testid={`area-${a.toLowerCase()}`}>
                  {a}
                </span>
              ))}
              <span className="rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-sm text-goldDark font-medium">& More</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="mt-12 rounded-2xl overflow-hidden border border-gold/30 shadow-xl bg-ivory/80 backdrop-blur-sm"
          data-testid="service-map"
        >
          <div className="flex items-center justify-between px-6 py-4 bg-ink">
            <p className="eyebrow text-gold">Coverage Map</p>
            <p className="text-ivory/60 text-xs font-light">Gold outline = my service zone</p>
          </div>
          <CoverageMap />
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceArea;
